// AyurvedaDynamic.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import styles from "../../assets/styles/Ayurveda/Ayurveda.module.css";
import { FaLeaf, FaFire, FaWater, FaWind, FaSpa, FaSeedling, FaSun, FaMoon, FaStar } from "react-icons/fa";
import { MdOutlineHealthAndSafety, MdRestaurant } from "react-icons/md";
import { GiHerbsBundle, GiOilPump, GiMeditation } from "react-icons/gi";

const iconMap = {
  FaWind, FaFire, FaWater, FaLeaf, FaSpa, FaSeedling, FaSun, FaMoon, FaStar,
  MdOutlineHealthAndSafety, MdRestaurant, GiHerbsBundle, GiOilPump, GiMeditation,
};

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API = `${ENV_API_BASE_URL}/api/ayurveda-page`;

const AyurvedaDynamic = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchAyurvedaData(); }, []);

  const fetchAyurvedaData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API);
      let data = res.data?.data || [];
      if (Array.isArray(data)) data = data[0] || {};
      ['connectionItems', 'benefitsItems', 'dinacharyaItems', 'therapyItems'].forEach(field => {
        if (data[field]) {
          if (typeof data[field] === 'string') {
            try { data[field] = JSON.parse(data[field]); } catch { data[field] = []; }
          }
        } else { data[field] = []; }
      });
      setPageData(data);
    } catch (err) {
      console.error("Failed to load Ayurveda content:", err);
      setError("Failed to load content. Please try again later.");
    } finally { setLoading(false); }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    if (imagePath.startsWith('/uploads')) return `${ENV_API_BASE_URL}${imagePath}`;
    return `${ENV_API_BASE_URL}/uploads/${imagePath}`;
  };

  const renderIcon = (iconName, className, color) => {
    if (!iconName) return null;
    const IconComponent = typeof iconName === 'string' ? iconMap[iconName] : iconName;
    return IconComponent ? <IconComponent className={className} style={color ? { color } : {}} /> : null;
  };

  if (loading) return (
    <div className={styles.pageWrapper}>
      <div className="container text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>
  );

  if (error || !pageData) return (
    <div className={styles.pageWrapper}>
      <div className="container text-center py-5">
        <div className="alert alert-danger" role="alert">{error || "No data available"}</div>
      </div>
    </div>
  );

  return (
    <div className={styles.pageWrapper}>

      {/* ── Hero ── */}
      <section
        className={`${styles.hero} d-flex align-items-center text-center`}
        style={pageData.heroImage ? { backgroundImage: `url(${getImageUrl(pageData.heroImage)})` } : {}}
      >
        <div className="container px-3">
          <h2 className={styles.sectionAyurveda}>{pageData.heroTitle || "Ayurveda"}</h2>
          <p className={`${styles.heroSubtitle} lead`}>{pageData.heroSubtitle || "The Science of Life & Longevity"}</p>
          {pageData.heroDescription && (
            <p className={`${styles.heroDesc} mt-4 mx-auto`}>{pageData.heroDescription}</p>
          )}
        </div>
      </section>

      {/* ── Introduction ── */}
      <section className={`${styles.section} py-5`}>
        <div className="container px-3 px-sm-4">
          <div className="row align-items-center gy-4">
            <div className="col-lg-6">
              <h2 className={styles.sectionTitle}>{pageData.whatIsAyurvedaHeading || "What is Ayurveda?"}</h2>
              <p className={styles.lead}>{pageData.whatIsAyurvedaParagraph}</p>
              {pageData.whatIsAyurvedaAdditional && (
                <p className={styles.lead}>{pageData.whatIsAyurvedaAdditional}</p>
              )}
            </div>
            <div className="col-lg-6">
              <img
                src={pageData.whatIsAyurvedaImage ? getImageUrl(pageData.whatIsAyurvedaImage) : "https://via.placeholder.com/600x400?text=Ayurveda+Image"}
                alt="Ayurvedic herbs and oil"
                className={`img-fluid rounded-3 shadow ${styles.image}`}
                onError={(e) => { e.target.src = "https://via.placeholder.com/600x400?text=Image+Not+Found"; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Three Doshas ── */}
      <section className={`py-5 ${styles.doshaSection}`}>
        <div className="container px-3 px-sm-4">
          <h2 className={`${styles.sectionTitle} text-center mb-5`}>The Three Doshas – Your Unique Blueprint</h2>
          <div className="row g-4 justify-content-center">
            {/* Vata */}
            <div className="col-sm-6 col-lg-4">
              <div className={`card h-100 ${styles.doshaCard}`}>
                <div className="card-body text-center">
                  {renderIcon(pageData.vataIcon, styles.doshaIcon, "#4a6fa5") || <FaWind className={styles.doshaIcon} style={{ color: "#4a6fa5" }} />}
                  <h3 className={styles.infoCardTitle}>{pageData.vataHeading || "Vata (Air & Space)"}</h3>
                  <p className={styles.infoCardSubTitle}>{pageData.vataDescription || "Governs movement, creativity, and communication. When balanced, you're energetic and flexible."}</p>
                  <p className="fw-bold">Balance tips:</p>
                  <ul className="list-unstyled">
                    {pageData.vataTips
                      ? pageData.vataTips.split('\n').map((tip, i) => <li key={i}>✓ {tip}</li>)
                      : (<><li>✓ Warm, nourishing foods</li><li>✓ Regular routine</li><li>✓ Abhyanga (self-massage)</li></>)}
                  </ul>
                </div>
              </div>
            </div>
            {/* Pitta */}
            <div className="col-sm-6 col-lg-4">
              <div className={`card h-100 ${styles.doshaCard}`}>
                <div className="card-body text-center">
                  {renderIcon(pageData.pittaIcon, styles.doshaIcon, "#e67e22") || <FaFire className={styles.doshaIcon} style={{ color: "#e67e22" }} />}
                  <h3 className={styles.infoCardTitle}>{pageData.pittaHeading || "Pitta (Fire & Water)"}</h3>
                  <p className={styles.infoCardSubTitle}>{pageData.pittaDescription || "Controls digestion, metabolism, and transformation. Balanced pitta brings intelligence."}</p>
                  <p className="fw-bold">Balance tips:</p>
                  <ul className="list-unstyled">
                    {pageData.pittaTips
                      ? pageData.pittaTips.split('\n').map((tip, i) => <li key={i}>✓ {tip}</li>)
                      : (<><li>✓ Cooling foods (cucumber, mint)</li><li>✓ Moderate exercise</li><li>✓ Time in nature</li></>)}
                  </ul>
                </div>
              </div>
            </div>
            {/* Kapha */}
            <div className="col-sm-6 col-lg-4">
              <div className={`card h-100 ${styles.doshaCard}`}>
                <div className="card-body text-center">
                  {renderIcon(pageData.kaphaIcon, styles.doshaIcon, "#2c7a7b") || <FaWater className={styles.doshaIcon} style={{ color: "#2c7a7b" }} />}
                  <h3 className={styles.infoCardTitle}>{pageData.kaphaHeading || "Kapha (Earth & Water)"}</h3>
                  <p className={styles.infoCardSubTitle}>{pageData.kaphaDescription || "Provides structure, stability, and lubrication. Balanced kapha creates calm and strength."}</p>
                  <p className="fw-bold">Balance tips:</p>
                  <ul className="list-unstyled">
                    {pageData.kaphaTips
                      ? pageData.kaphaTips.split('\n').map((tip, i) => <li key={i}>✓ {tip}</li>)
                      : (<><li>✓ Light, spicy foods</li><li>✓ Invigorating exercise</li><li>✓ Variety in routine</li></>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Ayurvedic Therapies — col-6 (2 per row on mobile, 4 per row on lg) ── */}
      {pageData.therapyItems?.length > 0 && (
        <section className={`${styles.section} py-5`}>
          <div className="container px-3 px-sm-4">
            <h2 className={`${styles.sectionTitle} text-center mb-5`}>Our Ayurvedic Therapies</h2>
            <div className="row g-3 g-md-4">
              {pageData.therapyItems.map((therapy, index) => (
                /* col-6 = 2 per row on all screens, col-lg-3 = 4 per row on lg+ */
                <div className="col-6 col-lg-3" key={index}>
                  <div className={`card h-100 ${styles.therapyCard}`}>
                    {renderIcon(therapy.icon, styles.therapyIcon) || <GiOilPump className={styles.therapyIcon} />}
                    <h3 className={styles.therapyCardTitle}>{therapy.heading}</h3>
                    <p className={styles.therapyCardDesc}>{therapy.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Ayurveda in Yoga Teacher Training ── */}
      <section className={`py-5 ${styles.integrationSection}`}>
        <div className="container px-3 px-sm-4">
          <div className="row align-items-center gy-4">
            <div className="col-lg-6 order-lg-2">
              <h2 className={styles.sectionTitle}>{pageData.teacherTrainingHeading || "Ayurveda in Our Yoga Teacher Training"}</h2>
              <p className={styles.infoCardSubTitle}>{pageData.teacherTrainingDesc || "Our Hatha Yoga training is enriched with studies in Ayurveda, Yoga Anatomy, Philosophy, and Mantra chanting."}</p>
              {pageData.teacherTrainingList && (
                <ul className={`list-unstyled ${styles.benefitList}`}>
                  {pageData.teacherTrainingList.split('\n').map((item, i) => (
                    <li key={i}><FaStar className="text-warning me-2" /> {item}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="col-lg-6 order-lg-1">
              <img
                src={pageData.teacherTrainingImage ? getImageUrl(pageData.teacherTrainingImage) : "https://via.placeholder.com/600x400?text=Yoga+and+Ayurveda"}
                alt="Yoga and Ayurveda"
                className={`img-fluid rounded-3 shadow ${styles.image}`}
                onError={(e) => { e.target.src = "https://via.placeholder.com/600x400?text=Image+Not+Found"; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Daily Routines (Dinacharya) ── */}
      {pageData.dinacharyaItems?.length > 0 && (
        <section className={`${styles.section} py-5`}>
          <div className="container px-3 px-sm-4">
            <h2 className={`${styles.sectionTitle} text-center mb-5`}>Dinacharya – Daily Ayurvedic Routines</h2>
            <div className="row gy-4">
              <div className="col-md-6">
                {pageData.dinacharyaItems.slice(0, Math.ceil(pageData.dinacharyaItems.length / 2)).map((item, index) => (
                  <div className={`d-flex mb-4 ${styles.routineItem}`} key={index}>
                    {renderIcon(item.icon, styles.routineIcon) || <FaSun className={styles.routineIcon} />}
                    <div>
                      <h3 className={styles.infoCardTitle}>{item.heading}</h3>
                      <p className={styles.infoCardSubTitle}>{item.paragraph}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="col-md-6">
                {pageData.dinacharyaItems.slice(Math.ceil(pageData.dinacharyaItems.length / 2)).map((item, index) => (
                  <div className={`d-flex mb-4 ${styles.routineItem}`} key={index}>
                    {renderIcon(item.icon, styles.routineIcon) || <FaMoon className={styles.routineIcon} />}
                    <div>
                      <h3 className={styles.infoCardTitle}>{item.heading}</h3>
                      <p className={styles.infoCardSubTitle}>{item.paragraph}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Seasonal Routines (Ritucharya) ── */}
      <section className={`py-5 ${styles.seasonalSection}`}>
        <div className="container px-3 px-sm-4">
          <h2 className={`${styles.sectionTitle} text-center mb-5`}>{pageData.seasonal || "Ritucharya – Seasonal Wisdom"}</h2>
          <div className="row gy-4 justify-content-center">
            {pageData.ritucharyaWinter && (
              <div className="col-sm-6 col-md-4">
                <div className={styles.seasonCard}>
                  <h3 className={styles.infoCardTitle}>{pageData.ritucharyaWinter}</h3>
                  <p className={styles.infoCardSubTitle}>{pageData.ritucharyaWinterParagraph || "Vata & Kapha accumulate. Favor warm, oily, sour, and salty foods. Stay active and keep warm."}</p>
                </div>
              </div>
            )}
            {pageData.ritucharyaSummer && (
              <div className="col-sm-6 col-md-4">
                <div className={styles.seasonCard}>
                  <h3 className={styles.infoCardTitle}>{pageData.ritucharyaSummer}</h3>
                  <p className={styles.infoCardSubTitle}>{pageData.ritucharyaSummerParagraph || "Pitta dominates. Eat cooling, sweet, bitter foods. Avoid excessive heat."}</p>
                </div>
              </div>
            )}
            {pageData.ritucharyaMonsoon && (
              <div className="col-sm-6 col-md-4">
                <div className={styles.seasonCard}>
                  <h3 className={styles.infoCardTitle}>{pageData.ritucharyaMonsoon}</h3>
                  <p className={styles.infoCardSubTitle}>{pageData.ritucharyaMonsoonParagraph || "Vata increases. Digestive fire weakens. Prefer light, warm, and easily digestible meals."}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Satvik Diet ── */}
      <section className={`${styles.section} py-5`}>
        <div className="container px-3 px-sm-4">
          <div className="row align-items-center gy-4">
            <div className="col-lg-6">
              <img
                src={pageData.FoodImage ? getImageUrl(pageData.FoodImage) : "https://via.placeholder.com/600x400?text=Satvik+Food"}
                alt="Healthy Satvik food"
                className={`img-fluid rounded-3 shadow ${styles.image}`}
                onError={(e) => { e.target.src = "https://via.placeholder.com/600x400?text=Image+Not+Found"; }}
              />
            </div>
            <div className="col-lg-6">
              <h2 className={styles.sectionTitle}>{pageData.Food || "Satvik Food – Eat Pure, Live Pure"}</h2>
              <p className={styles.infoCardSubTitle}>{pageData.foodPara || "Our ashram serves pure Satvik meals – fresh, seasonal, plant-based, and cooked with love."}</p>
              {pageData.foodPrinciplesHeading && (
                <>
                  <p className={styles.infoCardSubTitle}><strong>{pageData.foodPrinciplesHeading}</strong></p>
                  <ul>
                    {pageData.foodPrinciplesPara
                      ? pageData.foodPrinciplesPara.split('\n').map((p, i) => <li key={i}>{p}</li>)
                      : (<><li>Freshly prepared, never stale or processed.</li><li>Includes whole grains, legumes, vegetables, fruits, nuts, and dairy.</li><li>Spices used in moderation to aid digestion.</li><li>Avoids onion, garlic, and caffeine (Rajasic foods).</li></>)}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Benefits of Ayurveda ── */}
      <section className={`py-5 ${styles.benefitsSection}`}>
        <div className="container px-3 px-sm-4">
          <div className="row align-items-center gy-4">
            <div className="col-lg-6">
              <img
                src={pageData.benefitsImage ? getImageUrl(pageData.benefitsImage) : "https://via.placeholder.com/600x400?text=Ayurveda+Benefits"}
                alt="Ayurveda benefits"
                className={`img-fluid rounded-3 shadow ${styles.image}`}
                onError={(e) => { e.target.src = "https://via.placeholder.com/600x400?text=Image+Not+Found"; }}
              />
            </div>
            <div className="col-lg-6">
              <h2 className={styles.sectionTitle}>Benefits of Ayurveda</h2>
              <p className={styles.infoCardSubTitle}>{pageData.benefitsPara || "Integrating Ayurvedic principles into your life offers profound benefits:"}</p>
              <ul className={`list-unstyled ${styles.benefitList}`}>
                {pageData.benefitsItems?.length > 0
                  ? pageData.benefitsItems.map((benefit, index) => (
                    <li key={index}>
                      <span className={styles.benefitIcon}>{renderIcon(benefit.icon, "") || <FaLeaf />}</span>
                      <div><strong>{benefit.heading}:</strong> {benefit.listParagraph}</div>
                    </li>
                  ))
                  : (<>
                    <li><span className={styles.benefitIcon}><FaLeaf /></span><div><strong>Personalized Health:</strong> Learn to listen to your body and make choices that support your unique nature.</div></li>
                    <li><span className={styles.benefitIcon}><MdOutlineHealthAndSafety /></span><div><strong>Disease Prevention:</strong> Understand how imbalances develop and use lifestyle to correct them.</div></li>
                    <li><span className={styles.benefitIcon}><FaFire /></span><div><strong>Enhanced Digestion (Agni):</strong> Discover the central role of digestive fire in creating Ojas.</div></li>
                    <li><span className={styles.benefitIcon}><FaStar /></span><div><strong>Mental Clarity & Peace:</strong> Calm the mind by balancing the doshas through specific routines.</div></li>
                    <li><span className={styles.benefitIcon}><GiMeditation /></span><div><strong>Deeper Yoga Practice:</strong> Align your practice with the energies of the day and season.</div></li>
                  </>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Yoga & Ayurveda Connection ── */}
      {pageData.connectionItems?.length > 0 && (
        <section className={`py-5 ${styles.yogaConnectionSection}`}>
          <div className="container px-3 px-sm-4">
            <h2 className={`${styles.sectionTitle} text-center mb-4`}>{pageData.Connection || "The Connection: Yoga & Ayurveda"}</h2>
            <div className="row justify-content-center mb-5">
              <div className="col-lg-8 text-center">
                <p className={styles.infoCardSubTitle}>{pageData.ConnectionPara || "In our courses at our Rishikesh ashram, we teach Yoga and Ayurveda as complementary paths."}</p>
              </div>
            </div>
            <div className="row g-4 justify-content-center">
              {pageData.connectionItems.map((item, index) => (
                <div className="col-sm-6 col-6 col-md-4" key={index}>
                  <div className={`${styles.yogaConnectionCard} text-center`}>
                    {renderIcon(item.icon, styles.yogaConnectionIcon) || <FaWind className={styles.yogaConnectionIcon} />}
                    <h4 className={styles.infoCardTitle}>{item.heading}</h4>
                    <p className={styles.infoCardSubTitle}>{item.paragraph}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Call to Action ── */}
      <section className={`py-5 ${styles.ctaSection}`}>
        <div className="container px-3 text-center text-white">
          <h2 className={`${styles.ctaTitle} fw-bold mb-4`}>{pageData.ctaTitle || "Begin Your Ayurvedic Journey"}</h2>
          <p className={`${styles.ctaSubtitle} lead mb-4`}>{pageData.ctaSubtitle || "Fill the form and we will get back to you soon."}</p>
          <button className={`btn btn-lg ${styles.ctaButton}`}>{pageData.ctaButtonText || "Enquire Now"}</button>
          {pageData.contactInfo && <p className="mt-4 small">{pageData.contactInfo}</p>}
        </div>
      </section>

    </div>
  );
};

export default AyurvedaDynamic;